import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import premioElbb from 'assets/img/2026/premio_elbb.png';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativasPaginadas } from 'services/iniciativa.service';
import { Header } from '../HomePublic/components/Header';
import { HeroIniciativas } from './components/HeroIniciativas';
import { FiltrosIniciativas } from './components/FiltrosIniciativas';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import { CategoriaPremioId } from 'utils/iniciativa-categoria';
import './style.scss';

export function Iniciativas() {
  const iniciativasPorPagina = 20;
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(
    location.state?.categoria || 'todos'
  );
  const [filtroEtapa1, setFiltroEtapa1] = useState(location.state?.filtroEtapa1 || 'todas');
  const [termoBusca, setTermoBusca] = useState('');
  const [debouncedTermo, setDebouncedTermo] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedTermo(termoBusca.trim());
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [termoBusca]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [categoriaSelecionada, debouncedTermo, filtroEtapa1]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoading(true);
        const categoria = categoriaSelecionada !== 'todos'
          ? (categoriaSelecionada as CategoriaPremioId)
          : undefined;

        const classificadaEtapa1 = filtroEtapa1 === 'classificadas'
          ? true
          : filtroEtapa1 === 'nao-classificadas'
            ? false
            : undefined;

        const { items, total } = await buscarIniciativasPaginadas({
          userId: user?._id,
          page: paginaAtual,
          pageSize: iniciativasPorPagina,
          categoria,
          search: debouncedTermo || undefined,
          status: "inscrita",
          classificadaEtapa1,
        });

        setIniciativas(items);
        setTotalItems(total);
      } catch (error) {
        setIniciativas([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user?._id, paginaAtual, categoriaSelecionada, debouncedTermo, filtroEtapa1]);

  const totalPaginas = Math.max(1, Math.ceil(totalItems / iniciativasPorPagina));

  const iniciativasExibidas = iniciativas;

  const handleBuscar = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDebouncedTermo(termoBusca.trim());
  };

  const handlePaginaChange = (pagina: number) => {
    setPaginaAtual(pagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="iniciativas-wrapper">
      <Header currentPage="iniciativas" />

      <HeroIniciativas
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
      />

      <FiltrosIniciativas
        categoriaSelecionada=""
        onCategoriaChange={() => { }}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={handleBuscar}
        filtroEtapa1={filtroEtapa1}
        onFiltroEtapa1Change={setFiltroEtapa1}
      />

      <main className="iniciativas-main">
        <div className="iniciativas-main__container">
          <div className="iniciativas-main__header">
            <h2 className="iniciativas-main__count" tabIndex={0} aria-live="polite">
              {totalItems} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando iniciativas...</span>
            </div>
          ) : iniciativasExibidas.length === 0 ? (
            <div className="iniciativas-main__empty" role="status" aria-live="polite">
              <p className="iniciativas-main__empty-title">Nenhuma iniciativa encontrada para os filtros atuais.</p>
            </div>
          ) : (
            <>
              <div className="iniciativas-main__grid">
                {iniciativasExibidas.map((iniciativa: any) => {
                  const pos = iniciativa.position;
                  const podioPosition: 1 | 2 | 3 | undefined =
                    pos === 1 ? 1 : pos === 2 ? 2 : pos === 3 ? 3 : undefined;
                  return (
                    <IniciativaCard
                      key={iniciativa.id}
                      {...iniciativa}
                      podioPosition={podioPosition}
                      ocultarNaoValidos
                    />
                  );
                })}
                <aside className="premio-card" aria-label="Prêmio ELBB">
                  <div className="premio-card__inner">
                    <p className="premio-card__subtitle" tabIndex={0}>Conheça também as</p>
                    <p className="premio-card__subtitle" tabIndex={0}>iniciativas do</p>
                    <div className="premio-card__logo">
                      <img src={premioElbb} alt="Prêmio ELBB" tabIndex={0} />
                    </div>
                    <button className="premio-card__button" onClick={() => window.open('https://premioelbb.com.br/', '_blank')} aria-label="Quero conhecer o Prêmio ELBB">QUERO CONHECER!</button>
                  </div>
                </aside>
              </div>

              {totalPaginas > 1 && (
                <nav className="iniciativas-main__pagination" role="navigation" aria-label="Paginação de iniciativas">
                  <button
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => handlePaginaChange(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                  >
                    ‹
                  </button>

                  <div className="pagination__numbers" role="list">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                      <button
                        key={pagina}
                        className={`pagination__number ${paginaAtual === pagina ? 'active' : ''}`}
                        onClick={() => handlePaginaChange(pagina)}
                        aria-label={`Página ${pagina}`}
                        aria-current={paginaAtual === pagina ? 'page' : undefined}
                        role="listitem"
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination__btn pagination__btn--next"
                    onClick={() => handlePaginaChange(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
